/**
 * meta_nav.js
 * Single source of truth for navigation button configurations
 *
 * Supports multiple app-specific navigation configs with hierarchical primary/secondary structure
 * Integrated with MicrocopyManager for i18n support
 *
 * Navigation Structure:
 * - Each app has primary navigation items
 * - Each primary item can have secondary (sub-navigation) items
 * - Secondary items have the same schema as primary items
 *
 * Schema:
 * - token: string - Unique identifier (route name)
 * - caption: string - Display text (pulled from MicrocopyManager)
 * - enabled: boolean - Whether the item is active
 * - icon: string - CSS class string for icon (e.g., "fas fa-home")
 * - admin_role: boolean - Requires admin privileges
 * - tagline: string - Verbose description/tooltip (pulled from MicrocopyManager)
 * - secondary: array - Child navigation items (same schema)
 *
 * i18n Integration:
 * - Captions and taglines are retrieved from MicrocopyManager
 * - Token naming convention: nav_{lowercase_token} for captions
 * - Token naming convention: nav_{lowercase_token}_tagline for taglines
 * - Language changes are reflected via I18nController
 *
 * Current: Returns static navigation data
 * Future: Will fetch from coreMeta table for dynamic configuration
 *
 * Usage:
 *   import { NavMetaManager } from './meta_nav.js';
 *
 *   // Set app context
 *   NavMetaManager.setAppToken('client-saas');
 *
 *   // Get primary navigation
 *   const primaryNav = NavMetaManager.getPrimaryNav();
 *
 *   // Get flattened navigation (primary + all secondary)
 *   const allNav = NavMetaManager.getFlattenedNav();
 */

import { MicrocopyManager } from '../meta_microcopy_i18n.js';

export class NavMetaManager {
  static #appToken = null;

  /**
   * App-specific navigation configurations
   * Future: This will be fetched from coreMeta table
   */
  static #navConfigs = {
    "client-saas": [
      {
        token: "HOME",
        caption: "Home",
        enabled: true,
        icon: "--icon",
        admin_role: false,
        tagline: "Home Landing Page",
        secondary: [
          {
            token: "HOME_APPS",
            caption: "My Apps",
            enabled: true,
            icon: "--icon",
            admin_role: false,
            tagline: "My Apps",
          },
          {
            token: "HOME_DASH",
            caption: "Dashboard",
            enabled: true,
            icon: "--icon",
            admin_role: false,
            tagline: "Dashboard",
          }
        ]
      },
      {
        token: "ENGAGEMENTS",
        caption: "Engagements",
        enabled: true,
        icon: "var(--icon-engagements)",
        admin_role: false,
        tagline: "Customer engagement tools and interactions",
        secondary: [
          {
            token: "ENGAGEMENTS_SURVEYS",
            caption: "Surveys",
            enabled: true,
            icon: "var(--icon-surveys)",
            admin_role: false,
            tagline: "Create and manage customer surveys",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_THUMBS",
            caption: "Feedback",
            enabled: true,
            icon: "var(--icon-feedback)",
            admin_role: false,
            tagline: "Quick thumbs up/down feedback",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_VOICEMAIL",
            caption: "Voicemail",
            enabled: true,
            icon: "var(--icon-voicemail)",
            admin_role: false,
            tagline: "Voice message collection",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_GUIDEDTOURS",
            caption: "Guided Tours",
            enabled: true,
            icon: "var(--icon-guided-tours)",
            admin_role: false,
            tagline: "Interactive product tours",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_SLIDESHOWS",
            caption: "Slideshows",
            enabled: true,
            icon: "var(--icon-slideshows)",
            admin_role: false,
            tagline: "Content slideshows and presentations",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_ABTESTS",
            caption: "A/B Tests",
            enabled: true,
            icon: "var(--icon-ab-tests)",
            admin_role: false,
            tagline: "A/B testing campaigns",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_QUESTIONNAIRES",
            caption: "Questionnaires",
            enabled: true,
            icon: "var(--icon-questionnaires)",
            admin_role: false,
            tagline: "Detailed questionnaires and forms",
            secondary: []
          }
        ]
      },
      {
        token: "DEVELOP",
        caption: "Develop",
        enabled: true,
        icon: "var(--icon-develop)",
        admin_role: false,
        tagline: "Developer resources and tools",
        secondary: [
          {
            token: "DEVELOP_DOCUMENTATION",
            caption: "Documentation",
            enabled: true,
            icon: "var(--icon-documentation)",
            admin_role: false,
            tagline: "API and product documentation",
            secondary: []
          },
          {
            token: "DEVELOP_DEVTOOLS",
            caption: "Dev Tools",
            enabled: true,
            icon: "var(--icon-dev-tools)",
            admin_role: false,
            tagline: "Developer tools and utilities",
            secondary: []
          },
          {
            token: "DEVELOP_KBASE",
            caption: "Knowledge Base",
            enabled: true,
            icon: "var(--icon-knowledge-base)",
            admin_role: false,
            tagline: "Technical knowledge base and articles",
            secondary: []
          },
          {
            token: "DEVELOP_HACKATHON",
            caption: "Hackathon",
            enabled: true,
            icon: "var(--icon-hackathon)",
            admin_role: false,
            tagline: "Hackathon events and challenges",
            secondary: []
          },
          {
            token: "DEVELOP_PARTNER",
            caption: "Partner",
            enabled: true,
            icon: "var(--icon-partner)",
            admin_role: false,
            tagline: "Partner program and integrations",
            secondary: []
          },
          {
            token: "DEVELOP_CERTIFICATION",
            caption: "Certification",
            enabled: true,
            icon: "var(--icon-certification)",
            admin_role: false,
            tagline: "Developer certification programs",
            secondary: []
          }
        ]
      },
      {
        token: "PROFILE",
        caption: "Profile",
        enabled: true,
        icon: "var(--icon-profile)",
        admin_role: false,
        tagline: "Manage your personal profile and account",
        secondary: [
          {
            token: "PROFILE_ABOUTME",
            caption: "About Me",
            enabled: true,
            icon: "var(--icon-about-me)",
            admin_role: false,
            tagline: "View and edit your personal information",
            secondary: []
          },
          {
            token: "PROFILE_BADGES",
            caption: "Badges",
            enabled: true,
            icon: "var(--icon-badges)",
            admin_role: false,
            tagline: "View your earned badges and achievements",
            secondary: []
          },
          {
            token: "PROFILE_LANGUAGE",
            caption: "Language",
            enabled: true,
            icon: "var(--icon-profile-language)",
            admin_role: false,
            tagline: "Manage language settings and preferences",
            secondary: []
          },
          {
            token: "RESET_PASSWORD",
            caption: "Change Password",
            enabled: true,
            icon: "var(--icon-change-password)",
            admin_role: false,
            tagline: "Change your account password",
            secondary: []
          },
          {
            token: "SIGN_OUT",
            caption: "Sign Out",
            enabled: true,
            icon: "var(--icon-sign-out)",
            admin_role: false,
            tagline: "Log out of your account",
            secondary: []
          }
        ]
      },
      {
        token: "ACCOUNT",
        caption: "Account",
        enabled: true,
        icon: "var(--icon-account)",
        admin_role: false,
        tagline: "Account management and support",
        secondary: [
          {
            token: "ACCOUNT_CONTACT",
            caption: "Contact",
            enabled: true,
            icon: "var(--icon-contact)",
            admin_role: false,
            tagline: "Contact support and sales",
            secondary: []
          },
          {
            token: "ACCOUNT_PRICETIER",
            caption: "Price Tier",
            enabled: true,
            icon: "var(--icon-price-tier)",
            admin_role: false,
            tagline: "View and manage pricing plans",
            secondary: []
          },
          {
            token: "ACCOUNT_HELPFAQ",
            caption: "Help FAQ",
            enabled: true,
            icon: "var(--icon-help-faq)",
            admin_role: false,
            tagline: "Frequently asked questions",
            secondary: []
          },
          {
            token: "ACCOUNT_TEAM",
            caption: "Team",
            enabled: true,
            icon: "var(--icon-team)",
            admin_role: false,
            tagline: "Manage team members and roles",
            secondary: []
          },
          {
            token: "ACCOUNT_TERMS",
            caption: "Terms",
            enabled: true,
            icon: "var(--icon-terms)",
            admin_role: false,
            tagline: "Terms of service and legal documents",
            secondary: []
          }
        ]
      }
    ],

    "client-sse-console": [
      {
        token: "HOME",
        caption: "Home",
        enabled: true,
        icon: "fas fa-home",
        admin_role: false,
        tagline: "Admin console home dashboard",
        secondary: []
      },
      {
        token: "ENTITIES",
        caption: "Entities",
        enabled: true,
        icon: "fas fa-users",
        admin_role: false,
        tagline: "Entity and account management",
        secondary: [
          {
            token: "ACCOUNTS",
            caption: "Accounts",
            enabled: true,
            icon: "fas fa-user",
            admin_role: false,
            tagline: "User account management",
            secondary: []
          },
          {
            token: "GROUPS",
            caption: "Groups",
            enabled: true,
            icon: "fas fa-users-cog",
            admin_role: false,
            tagline: "Group and role management",
            secondary: []
          },
          {
            token: "INVOICES",
            caption: "Invoices",
            enabled: true,
            icon: "fas fa-file-invoice-dollar",
            admin_role: false,
            tagline: "Invoice and billing management",
            secondary: []
          }
        ]
      },
      {
        token: "CONTENT",
        caption: "Content",
        enabled: true,
        icon: "fas fa-folder-open",
        admin_role: false,
        tagline: "Content and asset management",
        secondary: [
          {
            token: "RSS",
            caption: "RSS",
            enabled: true,
            icon: "fas fa-rss",
            admin_role: false,
            tagline: "RSS feed management and syndication",
            secondary: []
          },
          {
            token: "CORE_ASSETS",
            caption: "Core Assets",
            enabled: true,
            icon: "fas fa-box",
            admin_role: false,
            tagline: "Manage core application assets and resources",
            secondary: []
          },
          {
            token: "DEEPLINKS",
            caption: "Deeplinks",
            enabled: true,
            icon: "fas fa-anchor",
            admin_role: false,
            tagline: "Deep link management and tracking",
            secondary: []
          },
          {
            token: "SNIPPETS",
            caption: "Snippets",
            enabled: true,
            icon: "fas fa-code",
            admin_role: false,
            tagline: "Code and content snippet management",
            secondary: []
          },
          {
            token: "ROLLERS",
            caption: "Rollers",
            enabled: true,
            icon: "fas fa-sync-alt",
            admin_role: false,
            tagline: "Rolling deployments and version management",
            secondary: []
          }
        ]
      },
      {
        token: "SUPPORT",
        caption: "Support",
        enabled: true,
        icon: "fas fa-life-ring",
        admin_role: false,
        tagline: "Support and monitoring tools",
        secondary: [
          {
            token: "DIALOG",
            caption: "Dialog",
            enabled: true,
            icon: "fas fa-comment-alt",
            admin_role: false,
            tagline: "Dialog and modal management",
            secondary: []
          },
          {
            token: "HEALTH",
            caption: "Health",
            enabled: true,
            icon: "fas fa-heartbeat",
            admin_role: false,
            tagline: "API health and authentication testing",
            secondary: []
          },
          {
            token: "METER",
            caption: "Meter",
            enabled: true,
            icon: "fas fa-tachometer-alt",
            admin_role: false,
            tagline: "Usage metering and analytics",
            secondary: []
          }
        ]
      },
      {
        token: "METADATA",
        caption: "Metadata",
        enabled: true,
        icon: "fas fa-cog",
        admin_role: false,
        tagline: "Metadata and configuration",
        secondary: [
          {
            token: "I18N",
            caption: "I18N",
            enabled: true,
            icon: "fas fa-language",
            admin_role: false,
            tagline: "Internationalization maintenance",
            secondary: []
          },
          {
            token: "META_MAINT",
            caption: "Meta Maint",
            enabled: true,
            icon: "fas fa-tools",
            admin_role: false,
            tagline: "Metadata maintenance and configuration",
            secondary: []
          }
        ]
      },
      {
        token: "SILOS",
        caption: "Silos",
        enabled: true,
        icon: "fas fa-database",
        admin_role: false,
        tagline: "Data silo management",
        secondary: [
          {
            token: "CACHE",
            caption: "Cache",
            enabled: true,
            icon: "fas fa-memory",
            admin_role: false,
            tagline: "Cache management and statistics",
            secondary: []
          },
          {
            token: "LIFECYCLE",
            caption: "Lifecycle",
            enabled: true,
            icon: "fas fa-recycle",
            admin_role: false,
            tagline: "Data lifecycle and retention policies",
            secondary: []
          }
        ]
      },
      {
        token: "NOTIFICATIONS",
        caption: "Notifications",
        enabled: true,
        icon: "fas fa-bell",
        admin_role: false,
        tagline: "Notification management",
        secondary: [
          {
            token: "BELL_DRAWER",
            caption: "Bell Drawer",
            enabled: true,
            icon: "fas fa-inbox",
            admin_role: false,
            tagline: "Notification drawer configuration",
            secondary: []
          },
          {
            token: "WEB_HOOKS",
            caption: "Web Hooks",
            enabled: true,
            icon: "fas fa-webhook",
            admin_role: false,
            tagline: "Webhook configuration and monitoring",
            secondary: []
          }
        ]
      },
      {
        token: "ACCOUNT",
        caption: "Account",
        enabled: true,
        icon: "fas fa-user-circle",
        admin_role: false,
        tagline: "Account settings and actions",
        secondary: [
          {
            token: "LANGUAGE",
            caption: "Language",
            enabled: true,
            icon: "fas fa-language",
            admin_role: false,
            tagline: "Change language preferences",
            secondary: []
          },
          {
            token: "RESET_PASSWORD",
            caption: "Reset Password",
            enabled: true,
            icon: "fas fa-key",
            admin_role: false,
            tagline: "Change your account password",
            secondary: []
          },
          {
            token: "SIGN_OUT",
            caption: "Sign Out",
            enabled: true,
            icon: "fas fa-sign-out-alt",
            admin_role: false,
            tagline: "Log out of your account",
            secondary: []
          }
        ]
      },
      {
        token: "ENGAGEMENTS",
        caption: "Engagements",
        enabled: true,
        icon: "fas fa-chart-line",
        admin_role: false,
        tagline: "User engagement tools",
        secondary: [
          {
            token: "AB_TESTS",
            caption: "A/B Tests",
            enabled: true,
            icon: "fas fa-vial",
            admin_role: false,
            tagline: "A/B testing campaigns",
            secondary: []
          },
          {
            token: "FEEDBACK",
            caption: "Feedback",
            enabled: true,
            icon: "fas fa-comments",
            admin_role: false,
            tagline: "User feedback collection",
            secondary: []
          },
          {
            token: "GUIDED_TOURS",
            caption: "Guided Tours",
            enabled: true,
            icon: "fas fa-route",
            admin_role: false,
            tagline: "Interactive product tours",
            secondary: []
          },
          {
            token: "QUESTIONNAIRES",
            caption: "Questionnaires",
            enabled: true,
            icon: "fas fa-clipboard-list",
            admin_role: false,
            tagline: "Detailed questionnaires and forms",
            secondary: []
          },
          {
            token: "SLIDESHOWS",
            caption: "Slideshows",
            enabled: true,
            icon: "fas fa-images",
            admin_role: false,
            tagline: "Content slideshows and presentations",
            secondary: []
          },
          {
            token: "SURVEYS",
            caption: "Surveys",
            enabled: true,
            icon: "fas fa-poll",
            admin_role: false,
            tagline: "Survey campaigns and analytics",
            secondary: []
          },
          {
            token: "VOICEMAIL",
            caption: "Voicemail",
            enabled: true,
            icon: "fas fa-voicemail",
            admin_role: false,
            tagline: "Voice message management",
            secondary: []
          }
        ]
      },
      {
        token: "ETHERCARDS",
        caption: "EtherCards",
        enabled: true,
        icon: "fas fa-credit-card",
        admin_role: false,
        tagline: "EtherCards platform management",
        secondary: [
          {
            token: "APPS",
            caption: "Apps",
            enabled: true,
            icon: "fas fa-mobile-alt",
            admin_role: false,
            tagline: "Application management",
            secondary: []
          },
          {
            token: "CHANNELS",
            caption: "Channels",
            enabled: true,
            icon: "fas fa-broadcast-tower",
            admin_role: false,
            tagline: "Communication channels",
            secondary: []
          },
          {
            token: "MESSAGES",
            caption: "Messages",
            enabled: true,
            icon: "fas fa-envelope",
            admin_role: false,
            tagline: "Message management and templates",
            secondary: []
          }
        ]
      }
    ],

    "LaunCh_console_applet": [
      {
        token: "CONSOLE_HOME",
        caption: "Console Home",
        enabled: true,
        icon: "fas fa-terminal",
        admin_role: false,
        tagline: "Launch console home dashboard",
        secondary: []
      },
      {
        token: "SYSTEM_MONITOR",
        caption: "System Monitor",
        enabled: true,
        icon: "fas fa-desktop",
        admin_role: false,
        tagline: "Real-time system monitoring and health checks",
        secondary: [
          {
            token: "SYSTEM_MONITOR_CPU",
            caption: "CPU Usage",
            enabled: true,
            icon: "fas fa-microchip",
            admin_role: false,
            tagline: "Monitor CPU performance and load",
            secondary: []
          },
          {
            token: "SYSTEM_MONITOR_MEMORY",
            caption: "Memory",
            enabled: true,
            icon: "fas fa-memory",
            admin_role: false,
            tagline: "Track memory usage and allocation",
            secondary: []
          },
          {
            token: "SYSTEM_MONITOR_NETWORK",
            caption: "Network",
            enabled: true,
            icon: "fas fa-network-wired",
            admin_role: false,
            tagline: "Network traffic and connectivity",
            secondary: []
          }
        ]
      },
      {
        token: "LOGS",
        caption: "Logs",
        enabled: true,
        icon: "fas fa-file-medical-alt",
        admin_role: false,
        tagline: "View system logs and error reports",
        secondary: [
          {
            token: "LOGS_APPLICATION",
            caption: "Application",
            enabled: true,
            icon: "fas fa-code",
            admin_role: false,
            tagline: "Application-level logs",
            secondary: []
          },
          {
            token: "LOGS_SYSTEM",
            caption: "System",
            enabled: true,
            icon: "fas fa-server",
            admin_role: false,
            tagline: "System-level logs",
            secondary: []
          },
          {
            token: "LOGS_SECURITY",
            caption: "Security",
            enabled: true,
            icon: "fas fa-lock",
            admin_role: false,
            tagline: "Security audit logs",
            secondary: []
          }
        ]
      },
      {
        token: "DEPLOYMENTS",
        caption: "Deployments",
        enabled: true,
        icon: "fas fa-rocket",
        admin_role: false,
        tagline: "Manage application deployments",
        secondary: []
      }
    ],

    "Client_Property_Sandbox_Emulator": [
      {
        token: "SANDBOX_HOME",
        caption: "Sandbox Home",
        enabled: true,
        icon: "fas fa-home",
        admin_role: false,
        tagline: "Sandbox testing environment home",
        secondary: []
      },
      {
        token: "API_TESTING",
        caption: "API Testing",
        enabled: true,
        icon: "fas fa-vial",
        admin_role: false,
        tagline: "Test API endpoints and responses",
        secondary: [
          {
            token: "API_TESTING_ENDPOINTS",
            caption: "Endpoints",
            enabled: true,
            icon: "fas fa-plug",
            admin_role: false,
            tagline: "Available API endpoints",
            secondary: []
          },
          {
            token: "API_TESTING_REQUESTS",
            caption: "Requests",
            enabled: true,
            icon: "fas fa-paper-plane",
            admin_role: false,
            tagline: "Send test requests",
            secondary: []
          },
          {
            token: "API_TESTING_HISTORY",
            caption: "History",
            enabled: true,
            icon: "fas fa-history",
            admin_role: false,
            tagline: "Request history and logs",
            secondary: []
          }
        ]
      },
      {
        token: "DATA_SIMULATOR",
        caption: "Data Simulator",
        enabled: true,
        icon: "fas fa-database",
        admin_role: false,
        tagline: "Simulate data scenarios and edge cases",
        secondary: [
          {
            token: "DATA_SIMULATOR_GENERATE",
            caption: "Generate",
            enabled: true,
            icon: "fas fa-magic",
            admin_role: false,
            tagline: "Generate mock data",
            secondary: []
          },
          {
            token: "DATA_SIMULATOR_LOAD",
            caption: "Load",
            enabled: true,
            icon: "fas fa-upload",
            admin_role: false,
            tagline: "Load data fixtures",
            secondary: []
          }
        ]
      },
      {
        token: "SCENARIOS",
        caption: "Scenarios",
        enabled: true,
        icon: "fas fa-project-diagram",
        admin_role: false,
        tagline: "Pre-configured test scenarios",
        secondary: []
      },
      {
        token: "RESET_SANDBOX",
        caption: "Reset Sandbox",
        enabled: true,
        icon: "fas fa-redo",
        admin_role: false,
        tagline: "Reset sandbox to initial state",
        secondary: []
      }
    ]
  };

  /**
   * Set the app token for context-aware navigation
   * @param {string} appToken - App identifier (e.g., 'client-saas')
   */
  static setAppToken(appToken) {
    if (!this.#navConfigs[appToken]) {
      console.warn(`[NavMetaManager] Unknown app token: ${appToken}. Available: ${Object.keys(this.#navConfigs).join(', ')}`);
    }
    this.#appToken = appToken;
    console.log(`[NavMetaManager] App token set: ${appToken}`);
  }

  /**
   * Get current app token
   * @returns {string|null} Current app token
   */
  static getAppToken() {
    return this.#appToken;
  }

  /**
   * Resolve i18n tokens for a navigation item
   * Converts token (e.g., "HOME") to microcopy tokens (e.g., "nav_home", "nav_home_tagline")
   * @param {Object} item - Navigation item
   * @returns {Object} Item with resolved caption and tagline
   * @private
   */
  static #resolveI18n(item) {
    if (!item) return item;

    const tokenLower = item.token.toLowerCase();
    const captionToken = `nav_${tokenLower}`;
    const taglineToken = `nav_${tokenLower}_tagline`;

    // Try to get i18n values, fallback to static values
    const caption = MicrocopyManager.get(captionToken) || item.caption;
    const tagline = MicrocopyManager.get(taglineToken) || item.tagline;

    return {
      ...item,
      caption,
      tagline,
      // Recursively resolve secondary items
      secondary: item.secondary ? item.secondary.map(s => this.#resolveI18n(s)) : []
    };
  }

  /**
   * Get primary navigation for current app with i18n resolved
   * @returns {Array} Array of primary navigation items with nested secondary
   */
  static getPrimaryNav() {
    if (!this.#appToken) {
      console.warn('[NavMetaManager] No app token set. Call setAppToken() first.');
      return [];
    }

    const navConfig = this.#navConfigs[this.#appToken] || [];

    // Resolve i18n tokens for all items
    return navConfig.map(item => this.#resolveI18n(item));
  }

  /**
   * Get flattened navigation (all primary + secondary items)
   * @returns {Array} Flattened array of all navigation items
   */
  static getFlattenedNav() {
    const primary = this.getPrimaryNav();
    const flattened = [];

    primary.forEach(item => {
      flattened.push(item);
      if (item.secondary && item.secondary.length > 0) {
        flattened.push(...item.secondary);
      }
    });

    return flattened;
  }

  /**
   * Get navigation item by token
   * @param {string} token - Navigation token
   * @returns {Object|null} Navigation item or null if not found
   */
  static getNavByToken(token) {
    const flattened = this.getFlattenedNav();
    return flattened.find(item => item.token === token) || null;
  }

  /**
   * Get admin-only navigation items
   * @returns {Array} Navigation items requiring admin role
   */
  static getAdminNav() {
    const flattened = this.getFlattenedNav();
    return flattened.filter(item => item.admin_role === true);
  }

  /**
   * Get user-accessible navigation items (non-admin)
   * @returns {Array} Navigation items accessible to regular users
   */
  static getUserNav() {
    const flattened = this.getFlattenedNav();
    return flattened.filter(item => item.admin_role === false);
  }

  /**
   * Get navigation as JSON string (for web component attributes)
   * @returns {string} JSON string of navigation
   */
  static getPrimaryNavJSON() {
    return JSON.stringify(this.getPrimaryNav());
  }

  /**
   * Get all available app tokens
   * @returns {Array<string>} List of app identifiers
   */
  static getAvailableApps() {
    return Object.keys(this.#navConfigs);
  }

  /**
   * Fetch navigation config from coreMeta table
   * Meta token format: NAV_{APP_TOKEN}
   * Example: NAV_SSE_SAAS_CLIENT
   *
   * @param {string} baseUrl - API base URL
   * @returns {Promise<Array>} Array of navigation configurations
   */
  static async fetchFromCoreMeta(baseUrl = '/m5t/v5') {
    if (!this.#appToken) {
      console.warn('[NavMetaManager] No app token set. Using static config.');
      return this.getPrimaryNav();
    }

    try {
      // Construct meta_token: NAV_SSE_SAAS_CLIENT
      const metaToken = `NAV_${this.#appToken.toUpperCase().replace(/-/g, '_')}`;

      const response = await fetch(`${baseUrl}/coreMeta?meta_token=${metaToken}`);

      if (!response.ok) {
        console.warn(`[NavMetaManager] Failed to fetch from coreMeta (${response.status}), using static config`);
        return this.getPrimaryNav();
      }

      const data = await response.json();

      // Extract navigation config from meta_keyval
      if (data.ok && data.data && data.data.meta_keyval) {
        console.log(`[NavMetaManager] Loaded navigation from coreMeta: ${metaToken}`);
        return data.data.meta_keyval;
      }

      console.warn('[NavMetaManager] No data in coreMeta response, using static config');
      return this.getPrimaryNav();

    } catch (error) {
      console.error('[NavMetaManager] Error fetching from coreMeta:', error);
      return this.getPrimaryNav();
    }
  }

  /**
   * Get all navigation configs for all apps
   * @returns {Object} All navigation configurations
   */
  static getAll() {
    return { ...this.#navConfigs };
  }
}

// CommonJS export for backwards compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { NavMetaManager };
}
